import { CircularProgress, Stack } from "@mui/material";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { SettingRow } from "@src/appV2/redesign/components/SettingRow";
import { Platform } from "@src/mobile/utils/platform";

import { Text } from "../../../components/Text";

interface AppInfoSectionViewProps {
  appVersion: string;
  buildNumber: string;
  bundleId: string | null;
  platform: string;
  environment: string;
  isLoading: boolean;
}

function InfoValue({ children }: { children: React.ReactNode }) {
  return (
    <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
      {children}
    </Text>
  );
}

function MonoValue({ children }: { children: React.ReactNode }) {
  return (
    <Text
      variant="body2"
      sx={(theme) => ({
        color: theme.palette.text.secondary,
        fontFamily: "monospace",
        fontSize: "0.75rem",
      })}
    >
      {children}
    </Text>
  );
}

function formatPlatform(platform: string, tu: (defaultText: string) => string): string {
  switch (platform) {
    case "ios": {
      return "iOS";
    }
    case "android": {
      return tu("Android");
    }
    default: {
      return tu("Web");
    }
  }
}

export function AppInfoSectionView({
  appVersion,
  buildNumber,
  bundleId,
  platform,
  environment,
  isLoading,
}: AppInfoSectionViewProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ paddingY: 4 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  return (
    <Stack>
      <SettingRow title={tu("Version")} action={<InfoValue>{appVersion}</InfoValue>} />
      {Platform.isMobile && (
        <SettingRow title={tu("Native Build")} action={<InfoValue>{buildNumber}</InfoValue>} />
      )}
      <SettingRow
        title={tu("Platform")}
        action={<InfoValue>{formatPlatform(platform, tu)}</InfoValue>}
      />
      <SettingRow title={tu("Server")} action={<InfoValue>{environment}</InfoValue>} />
      {Platform.isMobile && (
        <SettingRow
          title={tu("OTA Bundle ID")}
          action={<MonoValue>{bundleId ?? tu("None")}</MonoValue>}
        />
      )}
    </Stack>
  );
}
